import React, { Dispatch, useMemo } from "react"

import {
  Box,
  FormControlLabel,
  List,
  Switch,
  Typography,
  Checkbox,
  CircularProgress,
} from "@mui/material"
import { IShortClassStudent } from "api/reactQuery/queries/shortClassList.types"
import { useDrag } from "react-dnd"
import { useTranslation } from "react-i18next"
import { ItemsTypes } from "utils/itemsTypes"

import { ICheckedPerson, IStudentsToChecked } from "../Jobs"
import MultipleBoardHire from "./multipleBoardHire"
import SingleStudent from "./SingleStudent"

interface IStudent {
  checked: boolean
  setChecked: Dispatch<React.SetStateAction<boolean>>
  students: IShortClassStudent[]
  checkedPerson: ICheckedPerson[]
  setCheckedPerson: Dispatch<React.SetStateAction<ICheckedPerson[]>>
  stateStudentsToChecked: {
    studentsToChecked: IStudentsToChecked[]
    setStudentsToChecked: Dispatch<React.SetStateAction<IStudentsToChecked[]>>
  }
  isRefetching: boolean
  handleCheckAll: (checked: boolean) => void
  handleCheckStudent: (studentId: number, checked: boolean) => void
}

const StudentBoard = ({
  setChecked,
  checked,
  students,
  checkedPerson,
  setCheckedPerson,
  stateStudentsToChecked,
  isRefetching,
  handleCheckAll,
  handleCheckStudent,
}: IStudent) => {
  const { setStudentsToChecked } = stateStudentsToChecked
  const { t } = useTranslation()

  const displayedStudentIds = useMemo(
    () => new Set(students.map(student => student.id)),
    [students]
  )

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemsTypes.STUDENT,
      item: () => checkedPerson,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [checkedPerson]
  )

  const toggleAll = () => {
    const newCheckedState = !isAllChecked()
    handleCheckAll(newCheckedState)
  }

  const getSelectAllState = () => {
    const { studentsToChecked } = stateStudentsToChecked
    const checkedDisplayedCount = studentsToChecked.filter(
      student => student.checked && displayedStudentIds.has(student.id)
    ).length
    const totalDisplayedCount = students.length
    
    return checkedDisplayedCount > 0 && checkedDisplayedCount < totalDisplayedCount
  }

  const isAllChecked = () => {
    const { studentsToChecked } = stateStudentsToChecked
    const displayedStudents = studentsToChecked.filter(
      student => displayedStudentIds.has(student.id)
    )
    
    return displayedStudents.length > 0 && displayedStudents.every(student => student.checked)
  }

  return (
    <List
      sx={{
        width: "100%",
        mt: 4,
        textAlign: "center",
      }}
    >
      <FormControlLabel
        sx={{ mr: "0", mb: 1 }}
        control={
          <Switch
            checked={checked ?? false}
            onChange={() => setChecked(!checked)}
          />
        }
        label={
          <Typography
            variant="body2"
            fontWeight="500"
            sx={{ textAlign: "left", ml: 1.5 }}
          >
            {t("jobs.showStudentsWithoutJob")}
          </Typography>
        }
      />

      {!students.length ? (
        <Typography mt={2}>{t("jobs.everyoneHasJob")}</Typography>
      ) : (
        <Box sx={{ position: "relative" }}>
          {!!checkedPerson.length && students.length > 1 && (
            <MultipleBoardHire
              drag={drag}
              setStudentsToChecked={setStudentsToChecked}
              setCheckedPerson={setCheckedPerson}
              checkedPerson={checkedPerson}
              isDragging={isDragging}
            />
          )}
          {students.length > 1 && (
            <Box
              display="flex"
              alignItems="center"
              mt={!!checkedPerson.length ? "8px" : "0"}
            >
              <Checkbox
                checked={isAllChecked()}
                onChange={toggleAll}
                indeterminate={getSelectAllState()}
                sx={{ width: "24px", height: "24px" }}
              />
              <Typography variant="body2">{t("jobs.allStudents")}</Typography>
            </Box>
          )}
          {students.map((student) => {
            return (
              <SingleStudent
                key={student.id}
                student={student}
                checkedPerson={checkedPerson}
                stateStudentsToChecked={stateStudentsToChecked}
                students={students}
                handleCheckStudent={handleCheckStudent}
              />
            )
          })}
          <span>{isRefetching && <CircularProgress size="16px" />}</span>
        </Box>
      )}
    </List>
  )
}

export default StudentBoard
